import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import { 
  GraduationCap, 
  ArrowLeft,
  FileText,
  Scale
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-stone-50" data-testid="terms-page">
      {/* Header */}
      <header className="bg-white border-b border-stone-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <Link to="/" className="flex items-center gap-2">
                <GraduationCap className="h-8 w-8 text-indigo-700" />
                <span className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">
                  Gifted Academy Tutors
                </span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-12 md:py-16 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center">
              <FileText className="h-8 w-8 text-indigo-700" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-medium text-slate-900">
                Terms of Use
              </h1>
              <p className="text-slate-500">Last updated: March 2026</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="prose prose-slate max-w-none">
            <div className="bg-white rounded-2xl border border-stone-200 p-8 md:p-12 space-y-8">
              
              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  1. Introduction
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Gifted Academy Tutors provides online academic tutoring services. By accessing or using our platform, you agree to be bound by these Terms of Use. Please read them carefully before using our services.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  2. Acceptance of Terms
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  By using this platform, users agree to comply with all platform rules and policies. If you do not agree to these terms, please do not use our services.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  3. Tutor Relationship
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Tutors working with Gifted Academy Tutors operate as independent contractors and are not employees of Gifted Academy Tutors. Tutors are responsible for their own tax obligations and are not entitled to employee benefits.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  4. Educational Services Disclaimer
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Gifted Academy Tutors provides educational assistance services. <strong>Academic results, grades, or exam performance are not guaranteed.</strong> While we strive to provide high-quality tutoring, individual results may vary based on student effort, engagement, and other factors beyond our control.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  5. User Conduct
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Users agree to use the platform respectfully and for educational purposes only. Users must not:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Engage in harassment, abuse, or inappropriate behavior</li>
                  <li>Share illegal, offensive, or harmful content</li>
                  <li>Record tutoring sessions without explicit written permission</li>
                  <li>Attempt to contact tutors outside of the platform for private arrangements</li>
                  <li>Misrepresent their identity or qualifications</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  6. Account Suspension and Termination
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Gifted Academy Tutors reserves the right to suspend or terminate accounts that violate platform policies. We may take this action at our sole discretion and without prior notice in cases of serious policy violations.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  7. Intellectual Property
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  All content, materials, and resources provided through Gifted Academy Tutors, including lesson notes, worksheets, and educational materials, are protected by intellectual property laws. Users may use these materials for personal educational purposes only and may not redistribute or commercialize them.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  8. Limitation of Liability
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  Gifted Academy Tutors shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of our services. Our total liability shall not exceed the amount paid by you for tutoring services in the twelve (12) months preceding the claim.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  9. Governing Law
                </h2>
                <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <div className="flex items-start gap-3">
                    <Scale className="h-5 w-5 text-slate-600 mt-1 flex-shrink-0" />
                    <p className="text-slate-600 leading-relaxed">
                      All disputes related to the use of the Gifted Academy Tutors platform or tutoring services will be governed by the <strong>laws of Ontario, Canada</strong>. Any legal proceedings must be handled in the courts located in Ontario, Canada.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  10. Changes to Terms
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  We reserve the right to modify these Terms of Use at any time. We will notify users of significant changes via email or through the platform. Continued use of our services after changes constitutes acceptance of the modified terms.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">
                  11. Contact Information
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  For questions about these Terms of Use, please contact us at:
                </p>
                <p className="text-indigo-700 font-medium mt-2">
                  contact@giftedacademytutors.ca
                </p>
              </div>

            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsPage;
